import { Typography } from '@recruit-robin/robin-theme/web-components';
import { AlertCircle, CheckCircle, InfoCircle, XCircle } from '@recruit-robin/robin-theme/web-icons';
import { motion } from 'motion/react';
import { useEffect } from 'react';

import { useDictionary } from '@/shared/lib/i18n';
import { CloseButton } from '@/shared/ui/close-button';
import { ToastVariant } from '../../lib';
import { toastVariants, useStyles } from './Toast.styles';
import { ToastProps } from './Toast.types';

const icon: Record<ToastVariant, React.ReactNode> = {
  info: <InfoCircle size={16} />,
  warning: <AlertCircle size={16} />,
  error: <XCircle size={16} />,
  success: <CheckCircle size={16} />,
};

export const Toast = ({ toast, onDismiss }: ToastProps) => {
  const dictionary = useDictionary();
  const classes = useStyles({ variant: toast.variant });

  const label: Record<ToastVariant, string> = {
    info: dictionary.info,
    warning: dictionary.warning,
    error: dictionary.error,
    success: dictionary.success,
  };

  useEffect(() => {
    if (toast.duration > 0 && !toast.persist) {
      const timeout = setTimeout(() => {
        onDismiss(toast.id);
      }, toast.duration);

      return () => {
        clearTimeout(timeout);
      };
    }
  }, []);

  return (
    <motion.div css={classes.root} variants={toastVariants} initial="initial" animate="animate" exit="exit" layout>
      <div css={classes.icon}>{icon[toast.variant]}</div>

      <div css={classes.message}>
        <Typography variant={(v) => v.heading[5]} color={(c) => c.neutral.variant[80]}>
          {toast.title ?? label[toast.variant]}
        </Typography>
        <Typography variant={(v) => v.supporting.helper} color={(c) => c.neutral.variant[60]}>
          {toast.message}
        </Typography>
      </div>

      <CloseButton customCss={classes.closeButton} onClose={() => onDismiss(toast.id)} size={16} />
    </motion.div>
  );
};
